import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import closeIco from 'assets/images/close-button.svg';
import IMG_LOCK from 'assets/images/menu/settings-menu/lock-closed-purple.svg';
import validateEmail from 'bl/validation/validateEmail.bl';
import validateName from 'bl/validation/validateName.bl';
import validateNameLength from 'bl/validation/validateNameLength.bl';
import validatePassword from 'bl/validation/validatePassword.bl';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Foldout from 'components/generic/Foldout.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import Space from 'components/generic/Space.component';
import { withTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import { selectPinUserIsPrimary } from 'selectors/simple';
import {
  AT_COGNITO_UPDATE_USER_ATTRIBUTES_VERIFIED_EMAIL,
  COGNITO_ATTRIBUTE,
  updateUserAttributes,
} from 'utils/cognito.util';

import { normalizeEmail } from 'lib/normalize';
import InputFieldContainer from 'onboarding/common/InputField';
import PasswordInputFieldContainer from 'onboarding/common/PasswordInputField';
import SectionContent from 'settings/online-booking/pages/SectionContent.component';
import SectionSeparator from 'settings/online-booking/pages/SectionSeparator.component';
import SectionTitle from 'settings/online-booking/pages/SectionTitle.component';

import CN from './UserSettings.module.scss';

class UserSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      userData: Object.assign({}, props.userData),
      passwordsInput: {
        old: '',
        first: '',
        second: '',
      },
      editPasswordLayout: false,
      errorMessage: '',
      userNameHasError: false,
      emailHasError: false,
      passwordHasError: true,
      passwordsDontMatch: false,
      isSaveUserDataButtonActive: true,
      isSavePasswordButtonActive: false,
      isSaving: false,
      saved: false,
      tooltip: false,
    };
  }

  onSwitchLayoutClick = () => {
    if (this.state.editPasswordLayout) {
      this.setState({
        passwordsInput: {
          old: '',
          first: '',
          second: '',
        },
      });
    }

    this.setState({
      saved: false,
      editPasswordLayout: !this.state.editPasswordLayout,
    });
  };

  onNameChange = (value) => {
    const userData = Object.assign({}, this.state.userData);
    userData.name = value;
    this.setState({ userData }, () => {
      this.checkButton();
    });
  };

  onPasswordChange = (value, input) => {
    if (this.state.passwordsDontMatch) {
      this.setState({ passwordsDontMatch: false });
    }

    const passwordsInput = Object.assign({}, this.state.passwordsInput);
    passwordsInput[input] = value;

    const passwordsDontMatch =
      passwordsInput.first.length > 0 &&
      passwordsInput.second.length > 0 &&
      passwordsInput.first !== passwordsInput.second;

    this.setState({ passwordsInput, passwordsDontMatch }, () => {
      this.checkButton();
    });
  };

  onEmailChange = (value) => {
    const userData = Object.assign({}, this.state.userData);
    userData.email = normalizeEmail(value);
    this.setState({ userData }, () => {
      this.checkButton();
    });
  };

  validateName = (value) => {
    if (validateName({ value })) {
      this.setState({ userNameHasError: false });
      return '';
    }
    this.setState({ userNameHasError: true });
    return this.props.t('common.errors.validation.noName');
  };

  validateNameLength = (value) => validateNameLength({ value });

  validateEmail = (value) => {
    if (validateEmail({ value })) {
      this.setState({ emailHasError: false });
      return '';
    }
    this.setState({ emailHasError: true });
    return this.props.t('common.errors.validation.notValidEmail');
  };

  validatePassword = (value) => {
    if (validatePassword({ value })) {
      this.setState({ passwordHasError: false });
      return '';
    }
    this.setState({ passwordHasError: true });
    return this.props.t('common.errors.validation.passwordErrorMsg');
  };

  saveUserSettings = () => {
    const updateAttrs = {
      name: this.state.userData.name,
      email: this.state.userData.email,
    };

    this.setState({ isSaving: true }, () => {
      updateUserAttributes({ cognitoUser: this.props.cognitoUser, attributes: updateAttrs })
        .then(() => {
          this.setState({ isSaving: false });
          this.setState({ saved: true });
          // FIXME: mace action creator for this action

          this.props.dispatch({
            type: AT_COGNITO_UPDATE_USER_ATTRIBUTES_VERIFIED_EMAIL,
            attributes: updateAttrs,
          });
        })
        .catch((e) => {
          this.setState({ isSaving: false });
          this.setState({ errorMessage: e.message || JSON.stringify(e) });
        });
    });
  };

  saveNewPassword = () => {
    if (this.state.passwordsInput.first !== this.state.passwordsInput.second) {
      this.setState({ passwordsDontMatch: true });
      return;
    }
    this.setState({ isSaving: true }, () => {
      this.props.cognitoUser.changePassword(this.state.passwordsInput.old, this.state.passwordsInput.first, (err) => {
        this.setState({ isSaving: false });
        this.onSwitchLayoutClick();
        if (err) {
          this.setState({ errorMessage: err.message || JSON.stringify(err) });
          return;
        }
        this.setState({
          saved: true,
        });
      });
    });
  };

  checkButton = () => {
    let saveUserEnabled = true;
    let savePasswordEnabled = true;
    if (
      !this.state.editPasswordLayout &&
      (this.state.userData.name.length === 0 ||
        this.state.userData.email.length === 0 ||
        this.state.userNameHasError ||
        this.state.emailHasError)
    ) {
      saveUserEnabled = false;
    }

    if (this.state.editPasswordLayout) {
      const { first, second } = this.state.passwordsInput;
      savePasswordEnabled = !this.state.passwordHasError && first.length > 0 && second.length > 0 && first === second;
    }

    this.setState({ isSaveUserDataButtonActive: saveUserEnabled, isSavePasswordButtonActive: savePasswordEnabled });
  };

  showTooltip = () => {
    this.setState({
      tooltip: true,
    });
    setTimeout(() => {
      this.setState({
        tooltip: false,
      });
    }, 3000);
  };

  render() {
    const { userPrimary, isDarkMode, t } = this.props;
    const { tooltip } = this.state;

    return (
      <div
        data-bem="UserSettings"
        className={cn({
          [CN.component]: true,
          [CN.dark]: isDarkMode,
        })}
      >
        <Foldout
          isOpen
          tag="OperatingHours__days"
          src={IMG_LOCK}
          width="md"
          title={t('staffAndBusiness.userSettings.foldoutTitle')}
        >
          <SectionSeparator />
          <SectionTitle value={t('staffAndBusiness.userSettings.title1')} />
          <SectionContent>
            {tooltip && <div className="tooltip-is-master">{t('staffAndBusiness.userSettings.tooltipText')}</div>}
            <div data-bem="UserSettings__profile--row" className={CN.row}>
              <div className={CN.inputContainer}>
                <InputFieldContainer
                  data={{
                    id: 'NameInput',
                    type: 'text',
                    label: t('staffAndBusiness.userSettings.fullName'),
                    placeholder: t('staffAndBusiness.userSettings.fullName'),
                    inputValue: `${this.state.userData.name}`,
                    inputMode: 'text',
                  }}
                  onChangeHandler={this.onNameChange}
                  validateChange={this.validateNameLength}
                  validateBlur={this.validateName}
                  readOnly={!userPrimary}
                />
              </div>
              <div className={CN.inputContainer}>
                <InputFieldContainer
                  data={{
                    id: 'EmailInput',
                    type: 'email',
                    label: t('staffAndBusiness.userSettings.emailAddress'),
                    placeholder: 'name@salon.com',
                    inputValue: `${this.state.userData.email}`,
                    inputMode: 'email',
                  }}
                  onChangeHandler={this.onEmailChange}
                  validateBlur={this.validateEmail}
                  readOnly={!userPrimary}
                />
              </div>
            </div>

            <div className={CN.buttonsContainer}>
              <Button variant="outline" xSpace onClick={userPrimary ? this.onSwitchLayoutClick : this.showTooltip}>
                {t('staffAndBusiness.userSettings.changePasswordButton')}
              </Button>
              <Button
                variant="primary"
                xSpace
                onClick={this.saveUserSettings}
                disabled={!this.state.isSaveUserDataButtonActive || this.state.isSaving}
              >
                <ResponsiveBox.Desktop>{t('staffAndBusiness.userSettings.saveChangesButton')}</ResponsiveBox.Desktop>
                <ResponsiveBox.Mobile>{t('common.buttons.saveUpp')}</ResponsiveBox.Mobile>
              </Button>
            </div>
          </SectionContent>

          {this.state.editPasswordLayout && (
            <>
              <SectionSeparator />
              <SectionTitle value={t('staffAndBusiness.userSettings.title2')} />
              <SectionContent>
                <div data-bem="UserSettings__password">
                  <div data-bem="UserSettings__password--row" className={CN.row}>
                    <div className={CN.inputContainer}>
                      <PasswordInputFieldContainer
                        data={{
                          id: 'PasswordInput-1',
                          type: 'password',
                          label: t('staffAndBusiness.userSettings.oldPassword'),
                          placeholder: '●●●●●●●●',
                          inputValue: '',
                          inputMode: 'text',
                        }}
                        onChangeHandler={(value) => {
                          this.onPasswordChange(value, 'old');
                        }}
                        allowAutocomplete={false}
                      />
                    </div>
                  </div>
                  <div data-bem="UserSettings__password--row" className={CN.row}>
                    <div className={CN.inputContainer}>
                      <PasswordInputFieldContainer
                        data={{
                          id: 'PasswordInput-2',
                          type: 'password',
                          label: t('staffAndBusiness.userSettings.newPassword'),
                          placeholder: '●●●●●●●●',
                          inputValue: '',
                          inputMode: 'text',
                        }}
                        allowAutocomplete={false}
                        onChangeHandler={(value) => {
                          this.onPasswordChange(value, 'first');
                        }}
                        validateBlur={this.validatePassword}
                      />
                    </div>
                    <div className={CN.inputContainer}>
                      <PasswordInputFieldContainer
                        data={{
                          id: 'PasswordInput-3',
                          type: 'password',
                          label: t('staffAndBusiness.userSettings.retypePassword'),
                          placeholder: '●●●●●●●●',
                          inputValue: '',
                          inputMode: 'text',
                        }}
                        onChangeHandler={(value) => {
                          this.onPasswordChange(value, 'second');
                        }}
                        allowAutocomplete={false}
                      />
                      {this.state.passwordsDontMatch && (
                        <span className={CN.error}>{t('staffAndBusiness.userSettings.passwordErrorMsg1')}</span>
                      )}
                    </div>
                  </div>

                  <div className={CN.buttonsContainer}>
                    <div className={CN.alertOuter}>
                      <AlertPanel tag="StripeAccountVerifierModal__alert" variant="alert">
                        <div>
                          <Space bold>{t('staffAndBusiness.userSettings.beAware')}</Space>
                          {t('staffAndBusiness.userSettings.changePasswordAlert')}
                        </div>
                      </AlertPanel>
                    </div>
                    <Button variant="outline" xSpace onClick={this.onSwitchLayoutClick}>
                      {t('common.buttons.cancelUpp')}
                    </Button>
                    <Button
                      variant="primary"
                      xSpace
                      onClick={this.saveNewPassword}
                      disabled={
                        !this.state.isSavePasswordButtonActive || this.state.passwordsDontMatch || this.state.isSaving
                      }
                    >
                      {t('staffAndBusiness.userSettings.savePasswordButton')}
                    </Button>
                  </div>
                </div>

                {this.state.saved && (
                  <div className="alert success animated fadeInDown" style={{ marginRight: 0 }}>
                    {t('staffAndBusiness.userSettings.profileUpdated')}
                    <span
                      className="close"
                      onClick={() => void this.setState({ saved: false })}
                      role="button"
                      tabIndex="0"
                    >
                      <img src={closeIco} alt={t('staffAndBusiness.userSettings.closeModal')} />
                    </span>
                  </div>
                )}
              </SectionContent>
            </>
          )}
        </Foldout>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  cognitoUser: state.cognito.user,
  userData: {
    name: state.cognito.attributes.name,
    email: state.cognito.attributes.email,
    salon_managed: state.cognito.attributes[COGNITO_ATTRIBUTE.salonsManage]
      ? JSON.parse(state.cognito.attributes[COGNITO_ATTRIBUTE.salonsManage])
      : [],
  },
  userPrimary: selectPinUserIsPrimary(state) !== false,
});

const mapDispatchToProps = (dispatch) => ({ dispatch });

const UserSettingsHoc = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(withTheme()(UserSettings)));

export default UserSettingsHoc;
